import type { CSSProperties, DragEvent, MouseEvent, ReactNode } from 'react';
import styles from './index.module.less';
import classNames from 'classnames';
import { useEffect, useRef } from 'react';

interface FJModalProps {
    visible: boolean;
    maskStyle?: CSSProperties;
    zIndex?: number;
    width?: number | string;
    height?: number | string;
    backgroundColor?: string;
    onCancel?: () => void;
    duration?: number;
    onDragEnter?: (e: DragEvent<HTMLDivElement>) => void;
    onShow?: () => void;
    onShown?: () => void;
    onHide?: () => void;
    onHidden?: () => void;
    children?: ReactNode;
    borderRadius?: number;
    boxShadow?: string;
    /** 声明式 API 预留，当前未使用 */
    keyboard?: boolean;
    mask?: boolean;
    maskClosable?: boolean;
}

const FJModal = ({
    visible,
    maskStyle,
    zIndex,
    width,
    height = 'auto',
    backgroundColor,
    onCancel,
    duration,
    onDragEnter = () => {},
    onShow,
    onShown,
    onHide,
    onHidden,
    children,
    borderRadius,
    boxShadow,
}: FJModalProps) => {
    let _showTimer: ReturnType<typeof setTimeout> | null = null;
    let _hideTimer: ReturnType<typeof setTimeout> | null = null;
    const modalRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        toggleContainer(!visible);
        document.body.addEventListener('keydown', _keyboardEventFunc);
        return () => {
            document.body.removeEventListener('keydown', _keyboardEventFunc);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- 挂载时同步一次，与历史实现一致
    }, []);
    useEffect(() => {
        toggleContainer();
        (window as Window & { hasModal?: boolean }).hasModal = visible;
    }, [visible]);
    const _clearTimer = () => {
        if (_showTimer) {
            clearTimeout(_showTimer);
        }
        if (_hideTimer) {
            clearTimeout(_hideTimer);
        }
    };
    const _keyboardEventFunc = (e: KeyboardEvent) => {
        switch (e.keyCode) {
            case 27:
                onCancel?.();
                break;
            default:
        }
    };
    const toggleContainer = (flush?: boolean) => {
        _clearTimer();
        const root = modalRef.current;
        if (!root) {
            return;
        }
        if (visible) {
            if (onShow) {
                onShow();
            }
            root.style.display = 'block';
            _showTimer = setTimeout(() => {
                if (onShown) {
                    onShown();
                }
            }, duration ?? 0);
        } else {
            if (flush) {
                root.style.display = 'none';
            } else {
                if (onHide) {
                    onHide();
                }
                _hideTimer = setTimeout(() => {
                    if (modalRef.current) modalRef.current.style.display = 'none';
                    if (onHidden) {
                        onHidden();
                    }
                }, duration ?? 0);
            }
        }
    };
    const contentHandleClick = (e: MouseEvent) => {
        e.stopPropagation();
    };
    const maskCls = classNames(styles.remodal_mask, visible ? styles.remodal_fade_in : styles.remodal_fade_out);

    const containerCls = classNames(
        styles.remodal_container,
        visible ? styles.remodal_zoom_modal_in : styles.remodal_zoom_modal_out,
    );
    return (
        <div
            className={styles.remodal_root}
            ref={modalRef}
            style={{
                zIndex,
            }}
            onDragEnter={onDragEnter}
        >
            <div
                className={maskCls}
                style={{
                    ...maskStyle,
                    animationDuration: `${duration ?? 0}ms`,
                }}
            />
            <div
                className={containerCls}
                style={{
                    animationDuration: `${duration ?? 0}ms`,
                }}
                onClick={onCancel}
            >
                <div
                    className={styles.remodal_content}
                    onClick={contentHandleClick}
                    style={{
                        width,
                        height,
                        backgroundColor,
                        borderRadius,
                        boxShadow,
                    }}
                >
                    {children}
                </div>
            </div>
        </div>
    );
};

export default FJModal;
